import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {Checkbox, FormControlLabel} from "@mui/material";
import YellowButton from "../components/common/buttons/YellowButton";
import SecondaryButton from "../components/common/buttons/SecondaryButton";
import {EXTERNAL_LINKS} from "../config/external-links";
import {isExcludedScreen} from "./analytics";
import {ConsentChoice, OPEN_CONSENT_SETTINGS_EVENT, readConsent, saveConsent} from "./consent";
import "./ConsentBanner.css";

// Cookie consent for the app. Replaces the old "I understand" notice, which
// informed but never asked. Accept and reject are equally prominent on the
// first layer. Reopened from the footer via openConsentSettings().
// TODO(team): legal review of the copy and of the linked cookie policy (it was
// written for the WordPress site) before release.
const ConsentBanner: React.FC = () => {
    const {t} = useTranslation();
    const [open, setOpen] = useState(() => !isExcludedScreen() && readConsent() === null);
    const [showDetails, setShowDetails] = useState(false);
    const [analytics, setAnalytics] = useState(false);
    const [marketing, setMarketing] = useState(false);

    useEffect(() => {
        const openSettings = () => {
            const stored = readConsent();
            setAnalytics(stored?.analytics ?? false);
            setMarketing(stored?.marketing ?? false);
            setShowDetails(true);
            setOpen(true);
        };
        window.addEventListener(OPEN_CONSENT_SETTINGS_EVENT, openSettings);
        return () => window.removeEventListener(OPEN_CONSENT_SETTINGS_EVENT, openSettings);
    }, []);

    if (!open) {
        return null;
    }

    const decide = (choice: ConsentChoice) => {
        saveConsent(choice);
        setOpen(false);
        setShowDetails(false);
    };

    return (
        <section className="consent-banner" aria-labelledby="consent-banner-title">
            <h2 id="consent-banner-title" className="consent-banner-title">{t("Your privacy")}</h2>
            <p className="consent-banner-text">
                {t("We use cookies that are necessary for the app to work. With your consent we also use analytics cookies (Google Analytics, Contentsquare) to understand how the app is used and to improve it, and marketing cookies to measure our campaigns. You can change your choice at any time in \"Cookie settings\" in the footer.")}{" "}
                <a href={EXTERNAL_LINKS.cookiePolicy} target="_blank" rel="noopener noreferrer">
                    {t("Cookie policy")}
                </a>
            </p>

            {showDetails && (
                <div className="consent-banner-categories">
                    <FormControlLabel
                        control={<Checkbox checked disabled />}
                        label={<CategoryLabel title={t("Necessary")} description={t("Required for signing in, security and remembering your settings. Always on.")} />}
                    />
                    <FormControlLabel
                        control={<Checkbox checked={analytics} onChange={(e) => setAnalytics(e.target.checked)} />}
                        label={<CategoryLabel title={t("Analytics")} description={t("Help us understand how the app is used (Google Analytics, Contentsquare).")} />}
                    />
                    <FormControlLabel
                        control={<Checkbox checked={marketing} onChange={(e) => setMarketing(e.target.checked)} />}
                        label={<CategoryLabel title={t("Marketing")} description={t("Measure how effective our advertising is (Google).")} />}
                    />
                </div>
            )}

            <div className="consent-banner-actions">
                {showDetails ? (
                    <SecondaryButton onClick={() => decide({analytics, marketing})}>{t("Save choices")}</SecondaryButton>
                ) : (
                    <SecondaryButton onClick={() => setShowDetails(true)}>{t("Customize")}</SecondaryButton>
                )}
                <span className="consent-banner-spacer" />
                <YellowButton onClick={() => decide({analytics: false, marketing: false})}>{t("Reject all")}</YellowButton>
                <YellowButton onClick={() => decide({analytics: true, marketing: true})}>{t("Accept all")}</YellowButton>
            </div>
        </section>
    );
};

const CategoryLabel: React.FC<{title: string; description: string}> = ({title, description}) => (
    <span className="consent-banner-category">
        <strong>{title}</strong>
        <span>{description}</span>
    </span>
);

export default ConsentBanner;
